import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IMergeField, Template } from '@feathr/blackbox';
import { CardContent, CardHeader, CardV2, EmptyState, Fieldset, Input } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

interface IProps {
  context?: 'drip' | 'singular';
  disabled?: boolean;
  template: Template;
}

function TemplateMergefieldDefaults({
  context = 'singular',
  disabled = false,
  template,
}: Readonly<IProps>): JSX.Element {
  const { CustomFields } = useStore();
  const { t } = useTranslation();
  const customFields = CustomFields.list({
    filters: { collection__in: ['Person', 'Partner'] },
    pagination: {
      page: 0,
      page_size: 1000,
    },
  });
  const usedMergefields = template.get('used_mergefields', []);
  const mergefields = template.getMergefields(customFields.models);
  /*
   * Resolve each used merge tag to its definition, dropping the primary email tag
   * (which has no editable default) and any tag that no longer maps to a known field.
   * `used_mergefields` is append-only, so it can outlive the tags actually in use.
   */
  const renderableMergefields = usedMergefields
    .map((value) => mergefields.find((mf) => mf.value === value))
    .filter((def): def is IMergeField => def !== undefined && !def.noDefault);

  function getHandleChange(def: IMergeField) {
    return (newValue?: string) => {
      const current = template.get('mergefield_defaults', {});
      template.set({
        mergefield_defaults: {
          ...current,
          [def.value]: newValue ?? '',
        },
      });
    };
  }

  /*
   * In the drip flow the "Default Merge Data" label is always rendered by the parent
   * (EditAutomation), so when no used tag resolves to an editable default (e.g. only
   * the primary email tag is used, or a tag's field was removed) we show the same
   * empty state the parent uses for zero tags instead of an empty fieldset.
   * The pinpoint contexts have the same blank-card issue but their intended empty
   * UX is a separate design decision, so they are intentionally left unchanged here.
   */
  if (context === 'drip' && renderableMergefields.length === 0) {
    return <EmptyState label={t('No merge tags to display')} />;
  }

  return (
    <CardV2 width={'full'}>
      {context === 'singular' && (
        <CardHeader
          description={t(
            'These values will be used to replace merge tags when the recipient does not have data for the corresponding field.',
          )}
          title={t('Default Merge Data')}
        />
      )}
      <CardContent>
        <Fieldset>
          {renderableMergefields.map((def) => {
            const currentDefault = template.get('mergefield_defaults', {})[def.value];
            const inputValue = currentDefault && currentDefault.length ? currentDefault : '';
            return (
              <Input
                disabled={disabled}
                key={def.value}
                label={def.name}
                onChange={getHandleChange(def)}
                type={'text'}
                value={inputValue}
              />
            );
          })}
        </Fieldset>
      </CardContent>
    </CardV2>
  );
}

export default observer(TemplateMergefieldDefaults);
